import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { apiRequest } from '../../api/client';

interface Company {
  id: string;
  name: string;
  slug: string;
  website: string | null;
  logoUrl: string | null;
  headquarters: string | null;
  averageRating: number | null;
  reviewCount: number;
  _count: {
    jobs: number;
  };
  createdAt: string;
}

interface EditingCompany {
  id: string;
  name: string;
  website: string;
  headquarters: string;
  linkedInUrl: string;
  twitterUrl: string;
  description: string;
}

const AdminCompanies: React.FC = () => {
  const { getToken } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const [selectedCompanies, setSelectedCompanies] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingCompany, setEditingCompany] = useState<EditingCompany | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(search && { search }),
      });

      const data = await apiRequest<{ companies: Company[]; pagination: { pages: number; total: number } }>(
        `/admin/companies?${params}`,
        {},
        token
      );
      setCompanies(data.companies);
      setTotalPages(data.pagination.pages);
      setTotalCompanies(data.pagination.total);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, page, search]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const openEditModal = async (companyId: string) => {
    try {
      const token = await getToken();
      const company = await apiRequest<any>(`/admin/companies/${companyId}`, {}, token);
      setEditingCompany({
        id: company.id,
        name: company.name || '',
        website: company.website || '',
        headquarters: company.headquarters || '',
        linkedInUrl: company.linkedInUrl || '',
        twitterUrl: company.twitterUrl || '',
        description: company.description || '',
      });
    } catch (err: any) {
      alert(err.message);
    }
  };

  const saveCompany = async () => {
    if (!editingCompany) return;

    try {
      setIsSaving(true);
      const token = await getToken();
      await apiRequest(`/admin/companies/${editingCompany.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: editingCompany.name,
          website: editingCompany.website || null,
          headquarters: editingCompany.headquarters || null,
          linkedInUrl: editingCompany.linkedInUrl || null,
          twitterUrl: editingCompany.twitterUrl || null,
          description: editingCompany.description || null,
        }),
      }, token);
      setEditingCompany(null);
      fetchCompanies();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const deleteCompany = async (companyId: string) => {
    if (!window.confirm('Delete this company? This will also delete all associated jobs and reviews.')) {
      return;
    }

    try {
      const token = await getToken();
      await apiRequest(`/admin/companies/${companyId}`, { method: 'DELETE' }, token);
      fetchCompanies();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const toggleSelectCompany = (companyId: string) => {
    setSelectedCompanies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(companyId)) {
        newSet.delete(companyId);
      } else {
        newSet.add(companyId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedCompanies.size === companies.length) {
      setSelectedCompanies(new Set());
    } else {
      setSelectedCompanies(new Set(companies.map(c => c.id)));
    }
  };

  const deleteSelected = async () => {
    if (selectedCompanies.size === 0) return;

    if (!window.confirm(`Delete ${selectedCompanies.size} selected company(ies)? This will also delete all associated jobs and reviews.`)) {
      return;
    }

    try {
      setIsDeleting(true);
      const token = await getToken();
      await apiRequest('/admin/companies/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ ids: Array.from(selectedCompanies) }),
      }, token);

      setSelectedCompanies(new Set());
      fetchCompanies();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const limit = 20;
  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, totalCompanies);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-bold text-amber-400">Companies Management</h1>
        <span className="text-cream-200/70">
          {totalCompanies > 0 ? `Showing ${startItem}-${endItem} of ${totalCompanies} companies` : '0 companies'}
        </span>
      </div>

      {/* Filters and Bulk Actions */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex gap-4">
          <input
            type="text"
            placeholder="Search companies..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="bg-oak-700 text-cream-100 px-3 py-2 rounded-lg border border-oak-600 flex-1 max-w-xs"
          />
        </div>

        {!loading && companies.length > 0 && (
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedCompanies.size === companies.length && companies.length > 0}
                onChange={toggleSelectAll}
                className="w-4 h-4 rounded border-oak-600 bg-oak-700 text-amber-500 focus:ring-amber-500"
              />
              <span className="text-cream-200 text-sm">
                {selectedCompanies.size === companies.length && companies.length > 0 ? 'Deselect All' : 'Select All'}
              </span>
            </label>

            {selectedCompanies.size > 0 && (
              <div className="flex items-center gap-3">
                <span className="text-cream-200/70 text-sm">
                  {selectedCompanies.size} selected
                </span>
                <button
                  onClick={deleteSelected}
                  disabled={isDeleting}
                  className="px-4 py-1.5 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {isDeleting ? 'Deleting...' : 'Delete Selected'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 text-red-400 mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-cream-200/50">Loading...</div>
      ) : companies.length === 0 ? (
        <div className="text-cream-200/50">No companies found</div>
      ) : (
        <div className="space-y-3">
          {companies.map((company) => (
            <div key={company.id} className="card-wood rounded-lg p-4 flex items-center gap-3">
              <input
                type="checkbox"
                checked={selectedCompanies.has(company.id)}
                onChange={() => toggleSelectCompany(company.id)}
                className="w-4 h-4 rounded border-oak-600 bg-oak-700 text-amber-500 focus:ring-amber-500 cursor-pointer"
              />

              {/* Logo */}
              <div className="w-10 h-10 bg-oak-600 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                {company.logoUrl ? (
                  <img src={company.logoUrl} alt={company.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-cream-200/50 text-lg font-bold">
                    {company.name.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-cream-200">{company.name}</span>
                  {company.averageRating && (
                    <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded flex items-center gap-1">
                      <span>&#9733;</span> {company.averageRating.toFixed(1)}
                    </span>
                  )}
                </div>
                <div className="text-sm text-cream-200/70">
                  {company.headquarters || 'No location'}
                  {company.website && (
                    <>
                      {' • '}
                      <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:underline">
                        Website
                      </a>
                    </>
                  )}
                </div>
                <div className="text-xs text-cream-200/50 mt-1">
                  {company._count.jobs} jobs • {company.reviewCount} reviews • Added {new Date(company.createdAt).toLocaleDateString()}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => openEditModal(company.id)}
                  className="text-xs bg-amber-500/20 text-amber-400 px-3 py-1 rounded hover:bg-amber-500/30"
                >
                  Edit
                </button>
                <button
                  onClick={() => deleteCompany(company.id)}
                  className="text-xs bg-red-500/20 text-red-400 px-3 py-1 rounded hover:bg-red-500/30"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1 bg-oak-700 text-cream-200 rounded disabled:opacity-50"
          >
            Prev
          </button>
          <span className="px-3 py-1 text-cream-200/70">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1 bg-oak-700 text-cream-200 rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Edit Modal */}
      {editingCompany && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-oak-800 rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-display font-bold text-amber-400 mb-4">Edit Company</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-cream-200/70 text-sm mb-1">Company Name</label>
                <input
                  type="text"
                  value={editingCompany.name}
                  onChange={(e) => setEditingCompany({ ...editingCompany, name: e.target.value })}
                  className="w-full bg-oak-700 text-cream-100 px-3 py-2 rounded-lg border border-oak-600"
                />
              </div>

              <div>
                <label className="block text-cream-200/70 text-sm mb-1">Website</label>
                <input
                  type="text"
                  value={editingCompany.website}
                  onChange={(e) => setEditingCompany({ ...editingCompany, website: e.target.value })}
                  placeholder="https://example.com"
                  className="w-full bg-oak-700 text-cream-100 px-3 py-2 rounded-lg border border-oak-600"
                />
              </div>

              <div>
                <label className="block text-cream-200/70 text-sm mb-1">Headquarters</label>
                <input
                  type="text"
                  value={editingCompany.headquarters}
                  onChange={(e) => setEditingCompany({ ...editingCompany, headquarters: e.target.value })}
                  placeholder="San Francisco, CA"
                  className="w-full bg-oak-700 text-cream-100 px-3 py-2 rounded-lg border border-oak-600"
                />
              </div>

              <div>
                <label className="block text-cream-200/70 text-sm mb-1">LinkedIn URL</label>
                <input
                  type="text"
                  value={editingCompany.linkedInUrl}
                  onChange={(e) => setEditingCompany({ ...editingCompany, linkedInUrl: e.target.value })}
                  placeholder="https://linkedin.com/company/..."
                  className="w-full bg-oak-700 text-cream-100 px-3 py-2 rounded-lg border border-oak-600"
                />
              </div>

              <div>
                <label className="block text-cream-200/70 text-sm mb-1">Twitter URL</label>
                <input
                  type="text"
                  value={editingCompany.twitterUrl}
                  onChange={(e) => setEditingCompany({ ...editingCompany, twitterUrl: e.target.value })}
                  placeholder="https://twitter.com/..."
                  className="w-full bg-oak-700 text-cream-100 px-3 py-2 rounded-lg border border-oak-600"
                />
              </div>

              <div>
                <label className="block text-cream-200/70 text-sm mb-1">Description</label>
                <textarea
                  value={editingCompany.description}
                  onChange={(e) => setEditingCompany({ ...editingCompany, description: e.target.value })}
                  rows={4}
                  className="w-full bg-oak-700 text-cream-100 px-3 py-2 rounded-lg border border-oak-600 resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setEditingCompany(null)}
                className="px-4 py-2 text-cream-200/70 hover:text-cream-200"
              >
                Cancel
              </button>
              <button
                onClick={saveCompany}
                disabled={isSaving || !editingCompany.name.trim()}
                className="px-4 py-2 bg-amber-500 text-oak-900 rounded-lg hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCompanies;
